/**
 * FixingRatesPanel
 *
 * Compact inline component for the Forward Expiries header bar.
 * Shows reference rate fields (USD Obs, UF, EUR BFIX) and a "Run Fixing"
 * button. Displayed when the current date view contains EUR or CLF trades.
 *
 * Each field:
 * - Fetches the existing rate for the selected date on mount / date change.
 * - Read-only when a rate already exists in Firestore.
 * - Editable when no rate exists, allowing manual entry.
 *
 * "Run Fixing" saves any manually-entered rates (idempotent), then calls
 * the fixing endpoint, then triggers a silent page refresh.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../locales/i18n';
import { saveReferenceRate, getReferenceRate, runFixing } from '../../services/marketConventionsService';
import { parseAmountInput } from '../../utils/amountFormatter';

const COLLECTION_USD_OBS = 'usd_obs';
const COLLECTION_UF = 'uf';
const COLLECTION_EUR_BFIX = 'bbg_11_am_eur_usd';

/** Allow digits and the locale decimal separator (dot or comma), capped at `maxDecimals` decimal places. */
const sanitizeDecimalInput = (value: string, maxDecimals: number): string => {
  const lang = i18n.language?.substring(0, 2);
  const decimalSep = (lang === 'es' || lang === 'pt') ? ',' : '.';
  const otherSep = decimalSep === ',' ? '.' : ',';

  // Strip everything except digits and the locale decimal separator
  const allowedChars = new RegExp(`[^0-9\\${decimalSep}]`, 'g');
  let clean = value.replace(otherSep, decimalSep).replace(allowedChars, '');

  // Only keep the first decimal separator
  const parts = clean.split(decimalSep);
  if (parts.length > 2) clean = parts[0] + decimalSep + parts.slice(1).join('');

  // Truncate decimals
  const sepIdx = clean.indexOf(decimalSep);
  if (sepIdx !== -1 && clean.length - sepIdx - 1 > maxDecimals) {
    clean = clean.substring(0, sepIdx + 1 + maxDecimals);
  }
  return clean;
};

interface FixingRatesPanelProps {
  clientId: string;
  selectedDate: string;       // YYYY-MM-DD
  onFixingComplete: () => void;
}

const FixingRatesPanel: React.FC<FixingRatesPanelProps> = ({ clientId, selectedDate, onFixingComplete }) => {
  const { t } = useTranslation();

  // USD Obs state
  const [savedUsdObs, setSavedUsdObs] = useState<number | null>(null);
  const [inputUsdObs, setInputUsdObs] = useState<string>('');

  // UF state
  const [savedUf, setSavedUf] = useState<number | null>(null);
  const [inputUf, setInputUf] = useState<string>('');

  // EUR BFIX state
  const [savedBfix, setSavedBfix] = useState<number | null>(null);
  const [inputBfix, setInputBfix] = useState<string>('');

  // Shared status
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState<string>('');

  // Fetch existing rates when selectedDate changes
  const fetchExistingRates = useCallback(async () => {
    if (!selectedDate) return;

    // Reset status
    setStatus('idle');
    setStatusMessage('');

    // Fetch all rates in parallel
    const [usdObsResult, ufResult, bfixResult] = await Promise.allSettled([
      getReferenceRate(COLLECTION_USD_OBS, selectedDate),
      getReferenceRate(COLLECTION_UF, selectedDate),
      getReferenceRate(COLLECTION_EUR_BFIX, selectedDate),
    ]);

    const lang = i18n.language?.substring(0, 2);
    const useComma = lang === 'es' || lang === 'pt';
    const formatRate = (rate: number) => useComma ? String(rate).replace('.', ',') : String(rate);

    // USD Obs
    if (usdObsResult.status === 'fulfilled' && usdObsResult.value.data?.rate != null) {
      setSavedUsdObs(usdObsResult.value.data.rate);
      setInputUsdObs(formatRate(usdObsResult.value.data.rate));
    } else {
      setSavedUsdObs(null);
      setInputUsdObs('');
    }

    // UF
    if (ufResult.status === 'fulfilled' && ufResult.value.data?.rate != null) {
      setSavedUf(ufResult.value.data.rate);
      setInputUf(formatRate(ufResult.value.data.rate));
    } else {
      setSavedUf(null);
      setInputUf('');
    }

    // EUR BFIX
    if (bfixResult.status === 'fulfilled' && bfixResult.value.data?.rate != null) {
      setSavedBfix(bfixResult.value.data.rate);
      setInputBfix(formatRate(bfixResult.value.data.rate));
    } else {
      setSavedBfix(null);
      setInputBfix('');
    }
  }, [selectedDate]);

  useEffect(() => {
    fetchExistingRates();
  }, [fetchExistingRates]);

  /** Validate a manually-entered field. Returns the parsed number, or null if empty/already saved. Throws on invalid input. */
  const validateManualInput = (input: string, saved: number | null, errorKey: string): number | null => {
    if (!input || saved != null) return null; // nothing to save
    const num = parseAmountInput(input);
    if (num === null || !isFinite(num) || num <= 0) {
      throw new Error(t(errorKey));
    }
    return num;
  };

  const handleRunFixing = async () => {
    // Validate only fields the user has manually entered
    let usdObsNum: number | null = null;
    let ufNum: number | null = null;
    let bfixNum: number | null = null;

    try {
      usdObsNum = validateManualInput(inputUsdObs, savedUsdObs, 'forwardMaturity.fixingRates.validationUsdObs');
      ufNum = validateManualInput(inputUf, savedUf, 'forwardMaturity.fixingRates.validationUf');
      bfixNum = validateManualInput(inputBfix, savedBfix, 'forwardMaturity.fixingRates.validationBfix');
    } catch (e: any) {
      setStatus('error');
      setStatusMessage(e.message);
      return;
    }

    setStatus('loading');
    setStatusMessage('');

    try {
      const lang = i18n.language?.substring(0, 2);
      const useComma = lang === 'es' || lang === 'pt';
      const formatRate = (rate: number) => useComma ? String(rate).replace('.', ',') : String(rate);

      // Save any manually-entered rates
      if (usdObsNum != null) {
        const resp = await saveReferenceRate(COLLECTION_USD_OBS, selectedDate, usdObsNum);
        if (!resp.success) { setStatus('error'); setStatusMessage(t('forwardMaturity.fixingRates.errorSaveRate')); return; }
        const savedRate = resp.data?.rate ?? usdObsNum;
        setSavedUsdObs(savedRate);
        setInputUsdObs(formatRate(savedRate));
      }

      if (ufNum != null) {
        const resp = await saveReferenceRate(COLLECTION_UF, selectedDate, ufNum);
        if (!resp.success) { setStatus('error'); setStatusMessage(t('forwardMaturity.fixingRates.errorSaveRate')); return; }
        const savedRate = resp.data?.rate ?? ufNum;
        setSavedUf(savedRate);
        setInputUf(formatRate(savedRate));
      }

      if (bfixNum != null) {
        const resp = await saveReferenceRate(COLLECTION_EUR_BFIX, selectedDate, bfixNum);
        if (!resp.success) { setStatus('error'); setStatusMessage(t('forwardMaturity.fixingRates.errorSaveRate')); return; }
        const savedRate = resp.data?.rate ?? bfixNum;
        setSavedBfix(savedRate);
        setInputBfix(formatRate(savedRate));
      }

      // Run fixing
      const fixingResponse = await runFixing(clientId, selectedDate);
      if (!fixingResponse.success) {
        setStatus('error');
        setStatusMessage(t('forwardMaturity.fixingRates.errorRunFixing'));
        return;
      }

      const fixingsRecorded = fixingResponse.data?.fixingsRecorded ?? 0;
      setStatus('success');
      setStatusMessage(t('forwardMaturity.fixingRates.successMessage', { count: fixingsRecorded }));

      // Silent refresh
      onFixingComplete();
    } catch {
      setStatus('error');
      setStatusMessage(t('forwardMaturity.fixingRates.errorRunFixing'));
    }
  };

  const isUsdObsReadOnly = savedUsdObs != null;
  const isUfReadOnly = savedUf != null;
  const isBfixReadOnly = savedBfix != null;
  const isRunning = status === 'loading';

  const inputStyle = (readOnly: boolean, hasError: boolean): React.CSSProperties => ({
    width: '90px',
    padding: '3px 6px',
    fontSize: '13px',
    backgroundColor: readOnly ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.1)',
    color: readOnly ? '#aaa' : '#fff',
    border: `1px solid ${hasError ? '#f44336' : 'rgba(255,255,255,0.2)'}`,
    borderRadius: '4px',
    outline: 'none',
    textAlign: 'right' as const,
  });

  const separator = <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '14px' }}>|</span>;

  return (
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '13px',
    }}>
      {/* USD Obs field */}
      <span style={{ color: '#fff', fontSize: '14px', whiteSpace: 'nowrap' }}>
        {t('forwardMaturity.fixingRates.usdObsLabel')}
      </span>
      <input
        type="text"
        value={inputUsdObs}
        onChange={(e) => setInputUsdObs(sanitizeDecimalInput(e.target.value, 2))}
        readOnly={isUsdObsReadOnly}
        placeholder=""
        style={inputStyle(isUsdObsReadOnly, false)}
      />

      {separator}

      {/* UF field */}
      <span style={{ color: '#fff', fontSize: '14px', whiteSpace: 'nowrap' }}>
        {t('forwardMaturity.fixingRates.ufLabel')}
      </span>
      <input
        type="text"
        value={inputUf}
        onChange={(e) => setInputUf(sanitizeDecimalInput(e.target.value, 2))}
        readOnly={isUfReadOnly}
        placeholder=""
        style={inputStyle(isUfReadOnly, false)}
      />

      {separator}

      {/* EUR BFIX field */}
      <span style={{ color: '#fff', fontSize: '14px', whiteSpace: 'nowrap' }}>
        {t('forwardMaturity.fixingRates.eurBfixLabel')}
      </span>
      <input
        type="text"
        value={inputBfix}
        onChange={(e) => setInputBfix(sanitizeDecimalInput(e.target.value, 5))}
        readOnly={isBfixReadOnly}
        placeholder=""
        style={inputStyle(isBfixReadOnly, false)}
      />

      {/* Run Fixing button */}
      <button
        onClick={handleRunFixing}
        disabled={isRunning}
        style={{
          padding: '3px 10px',
          fontSize: '12px',
          fontWeight: 600,
          backgroundColor: isRunning ? 'rgba(76,175,80,0.4)' : 'var(--accent-blue, #2196f3)',
          color: '#fff',
          border: 'none',
          borderRadius: '4px',
          cursor: isRunning ? 'not-allowed' : 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        {isRunning
          ? t('forwardMaturity.fixingRates.runningButton')
          : t('forwardMaturity.fixingRates.runFixingButton')
        }
      </button>

      {/* Status message */}
      {statusMessage && (
        <span style={{
          fontSize: '12px',
          color: status === 'error' ? '#f44336' : status === 'success' ? '#4caf50' : '#ffaa00',
          whiteSpace: 'nowrap',
        }}>
          {statusMessage}
        </span>
      )}
    </div>
  );
};

export default FixingRatesPanel;
